import React, { useMemo, useState } from 'react';
import { patientService } from '../services/api';
import { PACIENTE_FIELD_LABELS, formatAuditValue } from './fieldLabels';
import './EditRecordModal.css';

const buildFormFromPatient = (patient) => ({
  nombre: patient.nombre || '',
  genero: patient.genero || '',
  fecha_nacimiento: patient.fecha_nacimiento ? String(patient.fecha_nacimiento).slice(0, 10) : '',
  ocupacion: patient.ocupacion || '',
  domicilio: patient.domicilio || '',
  nombre_tutor: patient.nombre_tutor || '',
});

const EditPatientModal = ({ patient, onClose, onSaved }) => {
  const [initialForm] = useState(() => buildFormFromPatient(patient));
  const [form, setForm] = useState(() => buildFormFromPatient(patient));
  const [step, setStep] = useState('form');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const changedFields = useMemo(
    () => Object.keys(form).filter((field) => form[field] !== initialForm[field]),
    [form, initialForm]
  );

  const handleReview = () => {
    setError('');
    if (!form.nombre.trim() || !form.genero || !form.fecha_nacimiento) {
      setError('Nombre, género y fecha de nacimiento son requeridos');
      return;
    }
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await patientService.update(patient.id, {
        nombre: form.nombre.trim(),
        genero: form.genero,
        fecha_nacimiento: form.fecha_nacimiento,
        ocupacion: form.ocupacion === '' ? null : form.ocupacion,
        domicilio: form.domicilio === '' ? null : form.domicilio,
        nombre_tutor: form.nombre_tutor === '' ? null : form.nombre_tutor,
      });
      if (result.success) {
        onSaved();
      } else {
        setError(result.message || 'No se pudo guardar el paciente');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-modal-backdrop" role="presentation">
      <div className="edit-modal" role="dialog" aria-modal="true" aria-labelledby="edit-paciente-title">
        <div className="edit-modal-header">
          <h3 id="edit-paciente-title">
            {step === 'form' ? `Editar datos de ${patient.nombre}` : 'Confirmar cambios'}
          </h3>
          <button type="button" className="edit-modal-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        <div className="edit-modal-body">
          {error && <div className="edit-modal-error">{error}</div>}

          {step === 'form' && (
            <div className="edit-modal-section">
              <div className="edit-form-grid">
                <div className="edit-form-field full-width">
                  <label>{PACIENTE_FIELD_LABELS.nombre}</label>
                  <input type="text" value={form.nombre} onChange={(e) => setField('nombre', e.target.value)} />
                </div>
                <div className="edit-form-field">
                  <label>{PACIENTE_FIELD_LABELS.genero}</label>
                  <select value={form.genero} onChange={(e) => setField('genero', e.target.value)}>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                  </select>
                </div>
                <div className="edit-form-field">
                  <label>{PACIENTE_FIELD_LABELS.fecha_nacimiento}</label>
                  <input type="date" value={form.fecha_nacimiento} onChange={(e) => setField('fecha_nacimiento', e.target.value)} />
                </div>
                <div className="edit-form-field">
                  <label>{PACIENTE_FIELD_LABELS.ocupacion}</label>
                  <input type="text" value={form.ocupacion} onChange={(e) => setField('ocupacion', e.target.value)} />
                </div>
                <div className="edit-form-field full-width">
                  <label>{PACIENTE_FIELD_LABELS.domicilio}</label>
                  <textarea value={form.domicilio} onChange={(e) => setField('domicilio', e.target.value)} />
                </div>
                <div className="edit-form-field">
                  <label>{PACIENTE_FIELD_LABELS.nombre_tutor}</label>
                  <input type="text" value={form.nombre_tutor} onChange={(e) => setField('nombre_tutor', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 'confirm' && (
            changedFields.length === 0 ? (
              <p className="confirm-no-changes">No hay cambios por guardar.</p>
            ) : (
              <>
                <p className="confirm-intro">
                  Revisa los cambios antes de confirmar. Cada edición queda registrada con
                  fecha, hora y doctor para trazabilidad del expediente.
                </p>
                <table className="confirm-changes-table">
                  <thead>
                    <tr>
                      <th>Campo</th>
                      <th>Antes</th>
                      <th>Después</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changedFields.map((field) => (
                      <tr key={field}>
                        <td>{PACIENTE_FIELD_LABELS[field] || field}</td>
                        <td><span className="confirm-value-before">{formatAuditValue(initialForm[field])}</span></td>
                        <td><span className="confirm-value-after">{formatAuditValue(form[field])}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )
          )}
        </div>

        <div className="edit-modal-footer">
          {step === 'form' && (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReview}
                disabled={changedFields.length === 0}
              >
                Revisar cambios
              </button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setStep('form')} disabled={saving}>
                Volver a editar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={saving || changedFields.length === 0}
              >
                {saving ? 'Guardando...' : 'Confirmar'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditPatientModal;
